import { useEffect } from 'react';
import { clearAuth, getStoredUser } from '../../utils/auth';

// /logout — clears localStorage + sibling cookies and bounces to a safe
// destination. Used by sibling subdomains (landing page) so they can log
// the user out without holding the portal's JWT themselves.
//
// Destination preference order:
//   1. ?next=<absolute or root-relative URL>  (validated)
//   2. REACT_APP_LANDING_URL env var
//   3. Role-aware default — portal roles bounce back to /portal-login,
//      patients bounce back to /login (client 2026-08-28).
function safeNext(raw) {
  if (!raw || typeof raw !== 'string') return '';
  // Root-relative paths are always safe.
  if (raw.startsWith('/') && !raw.startsWith('//')) return raw;
  // For absolute URLs, only allow http/https on a host that shares the
  // configured COOKIE_DOMAIN (the parent we trust).
  try {
    const u = new URL(raw);
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return '';
    const parent = (process.env.REACT_APP_COOKIE_DOMAIN || '').replace(/^\.+/, '');
    if (!parent) return '';
    if (u.hostname === parent || u.hostname.endsWith(`.${parent}`)) return raw;
  } catch { /* ignore */ }
  return '';
}

const PORTAL_ROLES = new Set(['VENDOR', 'ADMIN', 'STAFF', 'SUPER_ADMIN']);

function defaultDestForRole(role) {
  return PORTAL_ROLES.has(role) ? '/portal-login' : '/login';
}

function Logout() {
  useEffect(() => {
    // Read the role BEFORE clearing localStorage, so we know where to
    // send them back.
    const user = getStoredUser();
    const roleDest = defaultDestForRole(user?.role);

    clearAuth();
    const params = new URLSearchParams(window.location.search);
    const next = safeNext(params.get('next'))
      || (process.env.REACT_APP_LANDING_URL || '')
      || roleDest;
    // Replace so the user can't navigate back into a logged-out session.
    window.location.replace(next);
  }, []);
  return null;
}

export default Logout;
